/** Helpers to keep QAQC chart Y-axes readable when a few points are extreme. */

export const Z_SCORE_AXIS_MIN = -4;
export const Z_SCORE_AXIS_MAX = 4;

const EPS = 1e-9;

export function clamp(n, lo, hi) {
  if (n == null || Number.isNaN(n)) return n;
  return Math.min(hi, Math.max(lo, n));
}

function quantileSorted(sorted, q) {
  if (sorted.length === 0) return 0;
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sorted[base + 1] === undefined) return sorted[base];
  return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
}

export function quantile(values, q) {
  const nums = values.filter((v) => v != null && !Number.isNaN(v)).slice().sort((a, b) => a - b);
  if (nums.length === 0) return null;
  return quantileSorted(nums, q);
}

/**
 * Symmetric-ish band from central bulk of data; expands to include `mustInclude` interval.
 */
export function robustYBounds(values, options = {}) {
  const {
    lowP = 0.02,
    highP = 0.98,
    padRatio = 0.08,
    minSpan = 1e-12,
    mustLo = null,
    mustHi = null
  } = options;

  const loQ = quantile(values, lowP);
  const hiQ = quantile(values, highP);
  if (loQ == null || hiQ == null) {
    return { lo: 0, hi: 1 };
  }

  let lo = loQ;
  let hi = hiQ;
  if (hi - lo < minSpan) {
    const mid = (lo + hi) / 2;
    lo = mid - minSpan / 2;
    hi = mid + minSpan / 2;
  }

  const span = hi - lo;
  const pad = Math.max(span * padRatio, minSpan * padRatio);
  lo -= pad;
  hi += pad;

  if (mustLo != null) lo = Math.min(lo, mustLo);
  if (mustHi != null) hi = Math.max(hi, mustHi);

  if (hi <= lo) {
    hi = lo + minSpan;
  }

  return { lo, hi };
}

export function augmentRowsZScorePlot(rows, zKey = 'zScore') {
  return rows.map((row) => {
    const z = row[zKey];
    if (typeof z !== 'number' || Number.isNaN(z)) {
      return { ...row, plotZScore: z, zScoreAxisLabel: null };
    }
    const plotZScore = clamp(z, Z_SCORE_AXIS_MIN, Z_SCORE_AXIS_MAX);
    const zScoreAxisLabel =
      Math.abs(z - plotZScore) > EPS ? `z=${z.toFixed(2)}` : null;
    return { ...row, plotZScore, zScoreAxisLabel };
  });
}

export function augmentRowsNumericPlot(rows, valueKey, lo, hi, fmt = (v) => String(v)) {
  return rows.map((row) => {
    const v = row[valueKey];
    if (typeof v !== 'number' || Number.isNaN(v)) {
      const k = `plot${valueKey.charAt(0).toUpperCase()}${valueKey.slice(1)}`;
      return { ...row, [k]: v, [`${valueKey}AxisLabel`]: null };
    }
    const plot = clamp(v, lo, hi);
    const plotKey = `plot${valueKey.charAt(0).toUpperCase()}${valueKey.slice(1)}`;
    const label =
      Math.abs(v - plot) > EPS ? fmt(v) : null;
    return { ...row, [plotKey]: plot, [`${valueKey}AxisLabel`]: label };
  });
}

/** For control standards "show values" mode — `bestValue` on assay scale. */
export function boundsForAssayValues(values, mustLo = null, mustHi = null) {
  const { lo, hi } = robustYBounds(values, {
    lowP: 0.03,
    highP: 0.97,
    padRatio: 0.06,
    minSpan: 1e-8,
    mustLo,
    mustHi
  });
  return { lo, hi };
}

export function computeRpdDomainMax(rpdValues, refMax = 40) {
  const nums = rpdValues.filter((v) => v != null && !Number.isNaN(v) && v >= 0);
  if (nums.length === 0) return refMax;
  const p98 = quantile(nums, 0.98);
  if (p98 == null) return refMax;
  const padded = p98 * 1.12 + 1;
  return Math.max(refMax, Math.min(padded, Math.max(...nums)));
}

export function augmentRowsRpdPlot(rows, rpdKey = 'rpdPct', yMax) {
  return rows.map((row) => {
    const v = row[rpdKey];
    if (typeof v !== 'number' || Number.isNaN(v)) {
      return { ...row, plotRpdPct: v, rpdAxisLabel: null };
    }
    const plotRpdPct = clamp(v, 0, yMax);
    const rpdAxisLabel =
      Math.abs(v - plotRpdPct) > EPS ? `${v.toFixed(1)}%` : null;
    return { ...row, plotRpdPct, rpdAxisLabel };
  });
}

export function augmentRollingRpdPlot(rows, key = 'rollingMeanRPD50', yMax) {
  return rows.map((row) => {
    const v = row[key];
    if (v == null || typeof v !== 'number' || Number.isNaN(v)) {
      return { ...row, [`plot${key.charAt(0).toUpperCase()}${key.slice(1)}`]: v };
    }
    const plotKey = `plot${key.charAt(0).toUpperCase()}${key.slice(1)}`;
    return { ...row, [plotKey]: clamp(v, 0, yMax) };
  });
}

export function boundsForBlanksLeftAxis(values, lowerControl, upperControl, meanValue = null) {
  const mustLo =
    lowerControl != null && !Number.isNaN(lowerControl)
      ? lowerControl
      : null;
  const mustHi =
    upperControl != null && !Number.isNaN(upperControl)
      ? upperControl
      : null;
  const b = robustYBounds(values, {
    lowP: 0.03,
    highP: 0.97,
    padRatio: 0.1,
    minSpan: 1e-10,
    mustLo,
    mustHi
  });
  if (meanValue != null && !Number.isNaN(meanValue)) {
    return {
      lo: Math.min(b.lo, meanValue),
      hi: Math.max(b.hi, meanValue)
    };
  }
  return b;
}

export function boundsForCarryoverPct(values) {
  return robustYBounds(values, {
    lowP: 0,
    highP: 0.98,
    padRatio: 0.08,
    minSpan: 1,
    mustLo: 0,
    mustHi: null
  });
}

/** Symmetric square domain for 1:1 duplicate plots. */
export function boundsFor11Scatter(origValues, dupValues, padRatio = 0.05) {
  const combined = [...origValues, ...dupValues].filter(
    (v) => v != null && !Number.isNaN(v)
  );
  if (combined.length === 0) return { lo: 0, hi: 1 };
  const { lo: loR, hi: hiR } = robustYBounds(combined, {
    lowP: 0.02,
    highP: 0.98,
    padRatio,
    minSpan: 1e-12
  });
  const span = Math.max(hiR - loR, 1e-12);
  const mid = (loR + hiR) / 2;
  const half = span / 2;
  return { lo: mid - half, hi: mid + half };
}

export function augment11PairPlot(rows, lo, hi) {
  return rows.map((row) => {
    const o = row.origValue;
    const d = row.dupValue;
    const plotOrig =
      typeof o === 'number' && !Number.isNaN(o) ? clamp(o, lo, hi) : o;
    const plotDup =
      typeof d === 'number' && !Number.isNaN(d) ? clamp(d, lo, hi) : d;
    const pair11Label =
      typeof o === 'number' &&
      typeof d === 'number' &&
      !Number.isNaN(o) &&
      !Number.isNaN(d) &&
      (Math.abs(o - plotOrig) > EPS || Math.abs(d - plotDup) > EPS)
        ? `${o.toFixed(3)}→${d.toFixed(3)}`
        : null;
    return { ...row, plotOrigValue: plotOrig, plotDupValue: plotDup, pair11Label };
  });
}

export function trendMeanZBounds(allMeans, hardMin = -1.25, hardMax = 1.25) {
  const { lo, hi } = robustYBounds(allMeans, {
    lowP: 0.1,
    highP: 0.9,
    padRatio: 0.15,
    minSpan: 0.05,
    mustLo: null,
    mustHi: null
  });
  return {
    lo: Math.max(hardMin, lo),
    hi: Math.min(hardMax, hi)
  };
}
